/**
 * Pure helpers for the per-user `ui_prefs` blob (#373) — the cross-device UI-state
 * store on user_preferences. Kept React-free + side-effect-free so the parse/merge
 * robustness (the bits that actually fail on bad data) is unit-testable without a
 * DOM/component harness. The Sidebar consumes these; the server shares the write
 * half verbatim (mergeUiPrefsSlice, used by PATCH /api/preferences).
 */

export type UiPrefs = Record<string, unknown>;

/**
 * The top-level slices the app owns, and the ONLY keys a write may name
 * (#3072 review). A stored blob can still carry an older key: the merge never
 * drops what it is not asked about, and pruning is a separate decision. What
 * this gates is the WRITE, so a bug or a hand-rolled request cannot grow the
 * per-user blob with keys nothing reads.
 *
 * One constant, used by the client helper and by PATCH /api/preferences, so
 * the two cannot disagree about what a valid slice name is.
 */
export const UI_PREFS_SLICES = ["app_icon", "collapsed", "cost_basis", "icc", "print", "tables", "wet"] as const;

export type UiPrefsSlice = (typeof UI_PREFS_SLICES)[number];

/**
 * The blob is one D1 TEXT column read on every page load, so it needs a
 * ceiling. 64 KB is roughly a thousand remembered table columns: far past any
 * real use, close enough to catch a runaway writer before the row is a
 * problem. Checked on the MERGED blob, because that is what gets stored.
 */
export const UI_PREFS_MAX_BYTES = 64 * 1024;

/** The patch keys that are not slices the app owns. Empty list = every key is known. */
export function unknownUiPrefsSlices(patch: UiPrefs): string[] {
  const known = new Set<string>(UI_PREFS_SLICES);
  return Object.keys(patch).filter((k) => !known.has(k));
}

/**
 * Normalise a stored ui_prefs value (a JSON string from the API, an already-parsed
 * object, or null/undefined) into a plain object. Malformed JSON, null, arrays and
 * primitives all degrade to `{}` — never throws, so a corrupt blob can't break hydration.
 */
export function parseUiPrefs(raw: string | UiPrefs | null | undefined): UiPrefs {
  if (raw == null) return {};
  let obj: unknown = raw;
  if (typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (obj === null || typeof obj !== "object" || Array.isArray(obj)) return {};
  return obj as UiPrefs;
}

/**
 * Pull the sidebar-section collapse map out of a parsed ui_prefs blob. Returns null
 * (not `{}`) when there's no usable `collapsed` object, so callers can distinguish
 * "nothing to hydrate" from "hydrate to empty".
 */
export function extractCollapsed(uiPrefs: UiPrefs): Record<string, boolean> | null {
  const collapsed = uiPrefs.collapsed;
  if (collapsed === null || typeof collapsed !== "object" || Array.isArray(collapsed)) return null;
  return collapsed as Record<string, boolean>;
}

/**
 * #1931 — per-view table preferences inside ui_prefs: column widths (px) and
 * hidden columns, keyed by a stable viewKey. The write goes through
 * mergeUiPrefsSlice below (`{ tables: { <viewKey>: prefs } }`), which is what
 * keeps every other view and every sibling ui_prefs key.
 */
export interface TableViewPrefs {
  /** column key → width px */
  w?: Record<string, number>;
  /** hidden column keys */
  h?: string[];
  /** active sort axis, persisted per view */
  sort?: { key: string; dir: "asc" | "desc" };
  /**
   * COLMOVE (#4332): the user's column order, as column keys. Applied by
   * applyColumnOrder (client/components/ui/column-order.ts), which ignores a
   * key the table no longer has and keeps a column missing from the list
   * near its default neighbours, so a stored order never hides a new column.
   */
  o?: string[];
  /**
   * RMRECPOLISH (#4398): the key of the last frozen column, the reader's
   * freeze edge (Raw Materials Records). A key the table does not have, or
   * has hidden, falls back to the table's default freeze where it is read.
   * Other tables ignore it.
   */
  f?: string;
}

export function extractTablePrefs(uiPrefs: UiPrefs, viewKey: string): TableViewPrefs | null {
  const tables = uiPrefs.tables;
  if (tables === null || typeof tables !== "object" || Array.isArray(tables)) return null;
  const view = (tables as Record<string, unknown>)[viewKey];
  if (view === null || typeof view !== "object" || Array.isArray(view)) return null;
  const { w, h, sort, o, f } = view as { w?: unknown; h?: unknown; sort?: unknown; o?: unknown; f?: unknown };
  const out: TableViewPrefs = {};
  if (w !== null && typeof w === "object" && !Array.isArray(w)) {
    out.w = Object.fromEntries(
      Object.entries(w as Record<string, unknown>).filter(([, v]) => typeof v === "number" && Number.isFinite(v) && (v as number) > 0),
    ) as Record<string, number>;
  }
  if (Array.isArray(h)) out.h = (h as unknown[]).filter((x): x is string => typeof x === "string");
  // Same repair as `h`, plus de-duplication: a key listed twice would draw
  // its column twice.
  if (Array.isArray(o)) out.o = [...new Set((o as unknown[]).filter((x): x is string => typeof x === "string"))];
  if (typeof f === "string" && f !== "") out.f = f;
  if (sort !== null && typeof sort === "object" && !Array.isArray(sort)) {
    const candidate = sort as { key?: unknown; dir?: unknown };
    if (typeof candidate.key === "string" && (candidate.dir === "asc" || candidate.dir === "desc")) {
      out.sort = { key: candidate.key, dir: candidate.dir };
    }
  }
  return out;
}


/**
 * #3072: the slice-write merge, shared verbatim by the client helper
 * (`patchUiPrefs`) and the server (`PATCH /api/preferences`). One function, so
 * what a caller predicts it stored is what the row holds.
 *
 * The contract, in three lines:
 *   1. A key the patch does not name is untouched.
 *   2. An object value deep-merges ONE level (`icc.p12`, `tables.records`,
 *      `collapsed.Production`); anything deeper, and every scalar or array,
 *      replaces wholesale.
 *   3. `null` deletes, at the top level and one level down.
 *
 * Why only one level: every slice in the blob today is keyed two deep (family,
 * viewKey, section label) and the value at that depth is a whole record the
 * owner rewrites as a unit. A deeper merge would make a removed field inside
 * one of those records impossible to clear.
 *
 * `undefined` is not a delete: JSON cannot carry it, so a caller that produces
 * one means "I have nothing to say about this key" rather than "remove it".
 */
function isPlainObject(value: unknown): value is UiPrefs {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function mergeUiPrefsSlice(base: UiPrefs, patch: UiPrefs): UiPrefs {
  const out: UiPrefs = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    if (value === null) {
      delete out[key];
      continue;
    }
    if (isPlainObject(value)) {
      const prev = isPlainObject(out[key]) ? { ...(out[key] as UiPrefs) } : {};
      for (const [innerKey, innerValue] of Object.entries(value)) {
        if (innerValue === undefined) continue;
        if (innerValue === null) delete prev[innerKey];
        else prev[innerKey] = innerValue;
      }
      out[key] = prev;
      continue;
    }
    out[key] = value;
  }
  return out;
}
